import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, ArrowRight, Sparkles, Coins } from "lucide-react";
import { Ticker } from "@/components/Shared";
import SEOHead from "@/components/SEOHead";
import { StoryRow, StoryBand } from "@/components/StoryBlock";
import { IMG } from "@/lib/images";
import { HERO_BG, TICKER_ITEMS, LAYERS, STAT_BAND } from "./home/constants";
import { PeopleTeaserSection, ForEveryoneSection, NetworkProofTeaserSection } from "./home/Sections";

export default function Home() {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <SEOHead
        fallback={{
          title: "Infrastructure for the circular economy",
          desc: "Waste is not the end of a product. It is the beginning of a new asset. Build, finance and scale circular infrastructure with Circularity Global.",
        }}
      />
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 grid-bg opacity-60 animate-gridglow" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/90 to-background" />
          <img
            src={HERO_BG}
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-25 mix-blend-luminosity"
            loading="eager"
          />
        </div>

        <div className="container-x pt-24 pb-20 md:pt-36 md:pb-28">
          <div className="flex items-center gap-3">
            <span className="pill" data-testid="hero-pill"><span className="inline-block h-1.5 w-1.5 rounded-full bg-signal-lime animate-pulse" /> {t("home.pill")}</span>
          </div>
          <h1 className="h-display mt-7 max-w-5xl text-foreground" data-testid="hero-title">
            {t("home.title_a")}
            <span className="block text-stroke">{t("home.title_b")}</span>
          </h1>
          <p className="mt-7 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed" data-testid="hero-sub">
            {t("home.subtitle")}
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/ai-tools/waste-to-resource" className="btn-primary" data-testid="hero-cta-engine">
              <Sparkles size={16} /> {t("home.cta_engine")}
            </Link>
            <Link to="/contact" className="btn-secondary" data-testid="hero-cta-join">
              {t("home.cta_join")} <ArrowUpRight size={16} />
            </Link>
          </div>

          {/* mini 30-second pitch */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {[
              [t("home.pitch_nodes_t"), t("home.pitch_nodes_s")],
              [t("home.pitch_hubs_t"), t("home.pitch_hubs_s")],
              [t("home.pitch_network_t"), t("home.pitch_network_s")],
            ].map(([title, sub], i) => (
              <div key={title} className="card-surface !p-5" data-testid={`pitch-${i}`}>
                <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal-lime">0{i + 1}</p>
                <p className="mt-3 font-display text-xl tracking-tight">{title}</p>
                <p className="mt-2 text-sm text-muted-foreground">{sub}</p>
              </div>
            ))}
          </div>
        </div>

        <Ticker items={TICKER_ITEMS} />
      </section>
      <section className="container-x py-24 md:py-32 relative">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5 md:sticky md:top-24">
            <p className="eyebrow">{t("home.why_eyebrow")}</p>
            <h2 className="h-editorial mt-6">
              {t("home.why_title_a")} <span className="text-signal-lime">{t("home.why_title_value")}</span> {t("home.why_title_b")}
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed">
              {t("home.why_body1")}
            </p>
            <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
              {t("home.why_body2")}
            </p>
          </div>

          <div className="md:col-span-7 grid gap-6">
            {[
              { n: "01", t: t("home.story_1_t"), b: t("home.story_1_b") },
              { n: "02", t: t("home.story_2_t"), b: t("home.story_2_b") },
              { n: "03", t: t("home.story_3_t"), b: t("home.story_3_b") },
              { n: "04", t: t("home.story_4_t"), b: t("home.story_4_b") },
            ].map((s) => (
              <div key={s.n} className="card-surface group">
                <div className="flex items-start gap-5">
                  <p className="font-mono text-2xl tracking-tight text-signal-lime">{s.n}</p>
                  <div>
                    <h3 className="font-display text-xl md:text-2xl tracking-tight">{s.t}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.b}</p>
                  </div>
                </div>
              </div>
            ))}
            <a href="#enter-the-network" className="mt-2 inline-flex items-center gap-2 text-sm text-signal-lime">
              {t("home.story_join_link")} <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* STORYTELLING — what circularity looks like in the real world */}
      <StoryBand
        image="https://customer-assets.emergentagent.com/job_circular-os/artifacts/1vf8ypwa_ChatGPT%20Image%20Jun%2019%2C%202026%2C%2004_18_26%20PM.png"
        alt="Circularity Hub-23 at dusk — solar canopy, EV charging, AI micro data centre next to a rural village"
        caption="The infrastructure for the next industrial cycle won't look like the last one."
      />

      <section className="container-x py-24 md:py-32 space-y-20 md:space-y-28">
        <StoryRow
          image="/circularity-network-duality.png"
          alt="The Circularity Network — a Hub and a Station sharing the same village, connected by a single flowing arc of data"
          eyebrow="The Circularity Network"
          title="Two infrastructure types. One operating network."
          body="Two products. One Network. A &lsquo;Hub&rsquo; runs circularity in its original sense — waste → wealth, every byproduct becomes the next process&rsquo;s input. A &lsquo;Station&rsquo; runs a newer kind of circularity: a shared-resource capital cycle of the kind the world&rsquo;s largest infrastructure players already operate amongst themselves — land, power, compute and capital pooled into one closed flow of shared ownership and shared revenue. We bring that same architecture to rural communities. Both sit on the same backbone — land, communities, offtakers, carbon credits, CSR, investors, MACCIA&rsquo;s OVOE — and the same financial spine: Granullo moves the money."
          caption="Maharashtra · 100 Village Circular Economy Pilot (MoU signed)"
          cta={{ label: "See the Network", to: "/network", testId: "home-story-cta-np" }}
          testId="home-story-source"
        />
        <StoryRow
          reverse
          image="https://customer-assets.emergentagent.com/job_circular-os/artifacts/myespkfw_image.png"
          alt="Circularity Hub — modular processing infrastructure aerial render"
          eyebrow="Circularity Hub™ · Resource Infrastructure"
          title="The Hub is where a community keeps the value of what it already produces."
          body="A pre-financed, pre-permitted, pre-contracted module: intake → sort → process → output. Recycling, biochar and material-recovery partners deploy on-site. Recovered materials, finished goods, biochar, energy and credits ship from day one."
          caption="Hub archetype · 5–25 TPD modular envelope"
          cta={{ label: "Explore the Hub OS", to: "/network?view=hub", testId: "home-story-cta-hub-os" }}
          testId="home-story-hub"
        />
        <StoryRow
          image="https://customer-assets.emergentagent.com/job_circular-os/artifacts/cp10jo0e_image.png"
          alt="Circularity Station — aerial of the live pilot deployment"
          eyebrow="Circularity Station™ · Digital Infrastructure"
          title="The Station is where rural land becomes revenue-generating digital infrastructure."
          body="A repeatable model on a quarter-acre footprint: substation-anchored power, EV charging, edge AI compute, site office and a trained local workforce. Circularity orchestrates land and partnerships. DeCharge brings the technology and operates the infrastructure."
          caption="Pilot site · Maharashtra · per-station model v1"
          cta={{ label: "Explore the Station OS", to: "/network?view=station", testId: "home-story-cta-station-os" }}
          testId="home-story-station"
        />
        <StoryRow
          reverse
          image="https://customer-assets.emergentagent.com/job_circular-os/artifacts/y2k4g4vi_image.png"
          alt="Village co-operative — community ownership of the Circularity Network"
          eyebrow="At the community"
          title="If the people running the infrastructure don&rsquo;t own a piece of it, it isn&rsquo;t circular."
          body="Foundation programmes — microloans, insurance, scholarships, income share, community equity — are wired into every Network deployment from day one, not retrofitted later. Same principle for Hubs and Stations."
          caption="Community-anchored deployment"
          cta={{ label: "Foundation programmes", to: "/foundation", testId: "home-story-cta-found" }}
          testId="home-story-community"
        />
      </section>


      {/* PEOPLE NETWORK teaser */}
      <PeopleTeaserSection />

      {/* CTAS BENTO */}
      <section className="container-x py-24 md:py-28">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <p className="eyebrow">{t("home.ctas_eyebrow")}</p>
            <h2 className="h-editorial mt-4 max-w-2xl">{t("home.ctas_title")}</h2>
          </div>
          <p className="max-w-sm text-sm text-muted-foreground">
            {t("home.ctas_sub")}
          </p>
        </div>
        <div className="mt-12 grid-bento">
          {[
            { label: t("home.cta_find_value"), desc: t("home.cta_find_value_d"), to: "/ai-tools/waste-to-resource", emphasis: true },
            { label: t("home.cta_build_hub"), desc: t("home.cta_build_hub_d"), to: "/ai-tools/hub-builder" },
            { label: t("home.cta_invest"), desc: t("home.cta_invest_d"), to: "/for/investors" },
            { label: t("home.cta_join"), desc: t("home.cta_join_d"), to: "/contact" },
          ].map((c) => (
            <Link
              to={c.to}
              key={c.label}
              data-testid={`home-cta-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`group card-surface md:col-span-3 relative overflow-hidden ${c.emphasis ? "lime-glow md:col-span-6" : ""}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className={`eyebrow ${c.emphasis ? "text-signal-lime" : ""}`}>{c.emphasis ? t("home.featured_label") : t("home.primary_action_label")}</p>
                  <h3 className="mt-3 font-display text-2xl md:text-3xl tracking-tight">{c.label}</h3>
                  <p className="mt-3 max-w-md text-sm text-muted-foreground">{c.desc}</p>
                </div>
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card/60 transition-transform group-hover:rotate-45">
                  <ArrowUpRight size={16} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* LAYERS */}
      <section className="container-x py-24 md:py-28">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-start">
          <div className="md:sticky md:top-24">
            <p className="eyebrow">{t("home.arch_eyebrow")}</p>
            <h2 className="h-editorial mt-4">{t("home.arch_title")}</h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed">
              {t("home.arch_sub")}
            </p>
            <Link to="/network" className="mt-8 inline-flex items-center gap-1 text-sm text-signal-lime hover:gap-2 transition-all" data-testid="home-network-link">
              {t("home.arch_more")} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid gap-4">
            {LAYERS.map(({ tag, icon: Icon, title, body }) => (
              <div key={tag} className="card-surface">
                <div className="flex items-center justify-between">
                  <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-muted-foreground">{tag}</p>
                  <Icon size={18} className="text-signal-lime" />
                </div>
                <h3 className="mt-4 font-display text-2xl tracking-tight">{title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="container-x py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
          {STAT_BAND.map((p) => (
            <div key={p.labelKey} className="card-surface text-center">
              <p className="text-hero">{p.stat}</p>
              <p className="mt-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">{t(`home.proofs.${p.labelKey}`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FOR EVERYONE */}
      <ForEveryoneSection />

      {/* ALREADY FORMING — Network Proof teaser */}
      <NetworkProofTeaserSection />

      {/* FINAL CTA */}
      <section className="container-x pb-32">
        <div className="card-surface relative overflow-hidden lime-glow !p-10 md:!p-16">
          <p className="eyebrow text-signal-lime">{t("home.final_eyebrow")}</p>
          <h2 className="h-editorial mt-6 max-w-3xl">
            {t("home.final_title")}
          </h2>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary" data-testid="final-cta-join"><Coins size={16} /> {t("home.cta_join")}</Link>
            <Link to="/for/investors" className="btn-secondary" data-testid="final-cta-invest">{t("home.final_cta_invest")} <ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
